/**
 * Condition cards (blocks/condition-cards.liquid).
 *
 * The cards are a native radio group. This element connects that group to the page's real variant picker
 * (<variant-picker>, assets/variant-picker.js) without touching the theme's own code:
 *
 *   card -> picker:  choosing a card checks the matching radio (or sets the matching <select>) inside the picker and
 *                    dispatches a bubbling "change" event on it, which is exactly the event a click on the standard
 *                    option buttons produces. The picker then fetches the new section, updates the URL (?variant=), and
 *                    the price, buy buttons, inventory and sticky bar update through the theme's product-select event.
 *   picker -> cards: a "change" event inside the picker, a DOM change of the picker (its radios are re-rendered
 *                    after each selection), the browser's back/forward cache and history navigation all re-read which
 *                    option values are selected in the picker and check the matching card.
 *
 * Sold-out conditions can be chosen (unless the block setting turns that off). The theme draws the Notify me panel
 * (blocks/sold-out-actions.liquid) on the server, for the variant the page was loaded on, and the picker only re-renders
 * itself, so choosing a sold-out condition in place would show a disabled Sold out button and no Notify me form. When
 * the chosen sold-out condition is not the one the page was rendered for (data-rendered-variant), the card first drives
 * the real picker as above and then reloads the page once on ?variant=<id>, so the server draws the sold-out state and
 * the Notify me panel for exactly that condition. Choosing an in-stock card never reloads (sold-out-actions hides its
 * panel on the product-select event). Focus is kept: the card that had focus gets it back after the reload (with its focus
 * ring for keyboard users), and the scroll position is the browser's own reload restoration.
 *
 * Quick-add modal: the modal on collection pages clones the product page markup through DOMParser, so scripts such as
 * this one never run there and the cards stay inert. The theme's own modal CSS and a rule in the block's stylesheet hide
 * the cards in the modal, and the standard picker stays visible. If this script ever does run inside the modal it does
 * nothing.
 *
 * If no picker can be found (for example it was removed from the template) a card choice falls back to a normal
 * page load of ?variant=<id>, so a choice is never lost.
 */

/**
 * @typedef {{ n: string, v: string }} CardOption
 * @typedef {{ input: HTMLInputElement, id: string, available: boolean, options: CardOption[] }} Card
 */

const FOCUS_KEY = 'cc-refocus';

class ConditionCards extends HTMLElement {
  /** @type {AbortController | undefined} */
  #abort;

  /** @type {MutationObserver | undefined} */
  #observer;

  /** @type {Element | null} */
  #observedPicker = null;

  #frame = 0;

  connectedCallback() {
    // The quick-add modal shows the standard picker instead; a copy of the cards in there must never act.
    if (this.closest('quick-add-dialog')) {
      this.dataset.inert = 'true';
      return;
    }

    this.#abort = new AbortController();
    const { signal } = this.#abort;

    this.addEventListener('change', this.#onCardChange, { signal });
    document.addEventListener('change', this.#onDocumentChange, { signal });
    window.addEventListener('pageshow', this.#schedule, { signal });
    window.addEventListener('popstate', this.#onPopState, { signal });

    // First sync once the page has been parsed: the browser may have restored radio state on back/forward.
    this.#schedule();
    this.#restoreFocus();
  }

  disconnectedCallback() {
    this.#abort?.abort();
    this.#observer?.disconnect();
    this.#observer = undefined;
    this.#observedPicker = null;
    cancelAnimationFrame(this.#frame);
  }

  /** Queues one sync for the next frame, so bursts of DOM changes cost a single pass. */
  #schedule = () => {
    cancelAnimationFrame(this.#frame);
    this.#frame = requestAnimationFrame(() => {
      this.#watchPicker();
      this.#syncFromPicker();
    });
  };

  /** @returns {Card[]} */
  #cards() {
    /** @type {Card[]} */
    const cards = [];

    for (const input of this.querySelectorAll('input.cc__input')) {
      if (!(input instanceof HTMLInputElement)) continue;

      /** @type {CardOption[]} */
      let options = [];
      try {
        const parsed = JSON.parse(input.dataset.options || '[]');
        if (Array.isArray(parsed)) options = parsed;
      } catch (_) {
        options = [];
      }

      cards.push({ input, id: input.value, available: input.dataset.available !== 'false', options });
    }

    return cards;
  }

  /** @returns {HTMLElement | null} The page's main variant picker for this product. */
  #getPicker() {
    const productId = this.dataset.productId;
    const section = this.closest('.shopify-section');

    for (const root of [section, document]) {
      if (!root) continue;

      const pickers = Array.from(root.querySelectorAll('variant-picker')).filter((el) => el instanceof HTMLElement);
      const match = pickers.find((picker) => picker.dataset.productId === productId);
      if (match) return match;

      // The only picker of this section is the one to follow, even if the product id changed on it.
      if (root === section && pickers.length === 1) return pickers[0] ?? null;
    }

    return null;
  }

  /**
   * Reads which option values are currently selected in the picker, whatever its style (buttons or dropdowns).
   * @param {HTMLElement} picker
   * @returns {Map<string, string>} Option name to selected value.
   */
  #readSelection(picker) {
    /** @type {Map<string, string>} */
    const selection = new Map();

    for (const input of picker.querySelectorAll('fieldset input[type="radio"]:checked')) {
      if (!(input instanceof HTMLInputElement)) continue;
      const name = input.dataset.optionName;
      if (name) selection.set(name, input.value);
    }

    for (const select of picker.querySelectorAll('select')) {
      if (!(select instanceof HTMLSelectElement)) continue;
      const option = select.selectedOptions[0];
      const name = option?.dataset.optionName;
      if (option && name) selection.set(name, option.value);
    }

    return selection;
  }

  /** Checks the card that matches what the picker has selected. */
  #syncFromPicker() {
    const picker = this.#getPicker();
    if (!picker) return;

    const selection = this.#readSelection(picker);
    if (selection.size === 0) return;

    const match = this.#cards().find(
      (card) =>
        card.options.length > 0 &&
        card.options.some((option) => selection.has(option.n)) &&
        card.options.every((option) => !selection.has(option.n) || selection.get(option.n) === option.v)
    );

    if (match && !match.input.checked) match.input.checked = true;
  }

  /** Keeps watching the picker, because the theme re-renders its contents after every selection. */
  #watchPicker() {
    const picker = this.#getPicker();
    if (picker === this.#observedPicker) return;

    this.#observer?.disconnect();
    this.#observedPicker = picker;
    if (!picker) return;

    if (!this.#observer) this.#observer = new MutationObserver(() => this.#schedule());
    this.#observer.observe(picker, {
      subtree: true,
      childList: true,
      attributes: true,
      attributeFilter: ['checked', 'selected', 'data-current-checked'],
    });
  }

  /**
   * A card was chosen (by click, tap or arrow key).
   * @param {Event} event
   */
  #onCardChange = (event) => {
    const input = event.target;
    if (!(input instanceof HTMLInputElement) || input.type !== 'radio' || !input.checked) return;

    const card = this.#cards().find((entry) => entry.input === input);
    if (!card) return;

    // Decide before selecting: the picker's own update must not change what "rendered for" means.
    const reload = this.#needsReload(card);

    // Always go through the real picker, like a click on the standard buttons. It returns false when it had to
    // navigate to ?variant= instead, and then there is nothing left to reload.
    const drivenPicker = this.#selectVariant(card);
    if (drivenPicker && reload) this.#reloadOn(card, input);
  };

  /**
   * Something changed somewhere on the page: if it was the variant picker, follow it.
   * @param {Event} event
   */
  #onDocumentChange = (event) => {
    const target = event.target;
    if (!(target instanceof Element) || this.contains(target)) return;

    const picker = this.#getPicker();
    if (picker && picker.contains(target)) this.#syncFromPicker();
  };

  /** The address bar's ?variant= changed through history navigation: follow it. */
  #onPopState = () => {
    const wanted = new URL(window.location.href).searchParams.get('variant');
    if (!wanted) return;

    const card = this.#cards().find((entry) => entry.id === wanted);
    if (card && !card.input.checked) {
      card.input.checked = true;
      this.#selectVariant(card);
    }
  };

  /**
   * Whether choosing this card needs a fresh page: a sold-out condition other than the one the server rendered the
   * page for, because only a server render draws the Notify me panel (with the right condition in it).
   * @param {Card} card
   * @returns {boolean}
   */
  #needsReload(card) {
    if (this.dataset.soldOutReload !== 'true') return false;
    if (card.available || card.id === this.dataset.renderedVariant) return false;

    // The theme editor re-renders by itself and must not be reloaded under the merchant.
    const shopify = /** @type {{ designMode?: boolean } | undefined} */ (/** @type {any} */ (window).Shopify);
    return !shopify?.designMode;
  }

  /**
   * Reloads the page once on ?variant=<card id>. The real picker has already selected the variant (so the form state the
   * browser restores after a reload matches the server render) and set the same address; the reload keeps the scroll
   * position.
   * @param {Card} card
   * @param {HTMLInputElement} input
   */
  #reloadOn(card, input) {
    try {
      // Whoever had focus on a card (keyboard, screen reader) is put back on it after the reload; "kb" says whether the
      // focus ring was showing, so the ring is only forced back for keyboard users.
      if (document.activeElement === input) {
        sessionStorage.setItem(FOCUS_KEY, JSON.stringify({ id: card.id, t: Date.now(), kb: input.matches(':focus-visible') }));
      }
    } catch (_) {
      // Storage can be blocked; the reload still works, only the focus is not restored.
    }

    // Cleared again if the page is somehow still here after a while (the reload was blocked or cancelled).
    this.setAttribute('aria-busy', 'true');
    window.setTimeout(() => this.removeAttribute('aria-busy'), 10000);

    const url = new URL(window.location.href);
    url.searchParams.set('variant', card.id);

    let replaced = false;
    try {
      window.history.replaceState(window.history.state, '', url.toString());
      replaced = true;
    } catch (_) {
      replaced = false;
    }

    if (replaced) {
      window.location.reload();
    } else {
      window.location.assign(url.toString());
    }
  }

  /** After a reload caused by choosing a sold-out card, whoever had focus on a card gets it back on that card. */
  #restoreFocus() {
    /** @type {{ id?: string, t?: number, kb?: boolean } | null} */
    let wanted = null;

    try {
      const raw = sessionStorage.getItem(FOCUS_KEY);
      if (!raw) return;
      sessionStorage.removeItem(FOCUS_KEY);
      wanted = JSON.parse(raw);
    } catch (_) {
      return;
    }

    if (!wanted || Date.now() - Number(wanted.t) > 20000) return;
    const id = String(wanted.id);

    requestAnimationFrame(() => {
      const card = this.#cards().find((entry) => entry.id === id);
      if (!card || card.input.disabled) return;

      const { input } = card;
      input.focus({ preventScroll: true });

      // A script-driven focus right after a page load does not show the browser's focus ring, so a keyboard user would
      // not see where they are: draw it until focus leaves the card.
      if (wanted?.kb === true) {
        input.setAttribute('data-refocused', '');
        input.addEventListener('blur', () => input.removeAttribute('data-refocused'), { once: true });
      }
    });
  }

  /**
   * Selects a variant in the real picker, the same way a click on its option buttons does.
   * @param {Card} card
   * @returns {boolean} True when the picker was driven, false when the page was sent to ?variant= instead.
   */
  #selectVariant(card) {
    const picker = this.#getPicker();
    /** @type {Array<{ control: HTMLInputElement | HTMLSelectElement, value: string }>} */
    const changes = [];
    let found = 0;

    if (picker) {
      const radios = Array.from(picker.querySelectorAll('fieldset input[type="radio"]'));
      const selects = Array.from(picker.querySelectorAll('select'));

      for (const option of card.options) {
        const radio = radios.find(
          (input) => input instanceof HTMLInputElement && input.dataset.optionName === option.n && input.value === option.v
        );

        if (radio instanceof HTMLInputElement) {
          found++;
          if (!radio.checked) changes.push({ control: radio, value: option.v });
          continue;
        }

        const select = selects.find(
          (element) => element instanceof HTMLSelectElement && element.options[0]?.dataset.optionName === option.n
        );

        if (select instanceof HTMLSelectElement) {
          found++;
          if (select.value !== option.v) changes.push({ control: select, value: option.v });
        }
      }
    }

    // No picker to drive: load the page with the chosen variant instead.
    if (found === 0) {
      const url = new URL(window.location.href);
      url.searchParams.set('variant', card.id);
      window.location.assign(url.toString());
      return false;
    }

    // Set every option of the variant first, then tell the picker once per changed control, so it never sees a
    // half-selected combination.
    for (const { control, value } of changes) {
      if (control instanceof HTMLInputElement) {
        control.checked = true;
      } else {
        control.value = value;
      }
    }

    for (const { control } of changes) {
      control.dispatchEvent(new Event('change', { bubbles: true }));
    }

    return true;
  }
}

if (!customElements.get('condition-cards')) {
  customElements.define('condition-cards', ConditionCards);
}
